import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../api/AuthContext";
import {
  FiBarChart2,
  FiDollarSign,
  FiHome,
  FiList,
  FiLogOut,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { ROLE_ADMIN, ROLE_USER_MAAL, hasRole } from "../utils/roleUtils";

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = hasRole(user, ROLE_ADMIN);
  const isMaal = hasRole(user, ROLE_USER_MAAL);

  const navItems = [
    { label: "Dashboard", to: "/", icon: <FiHome /> },
    isAdmin && {
      label: "Admin Transactions",
      to: "/admin/transactions",
      icon: <FiList />,
    },
    isAdmin && {
      label: "User Access Management",
      to: "/admin/user-access",
      icon: <FiUsers />,
    },
    !isAdmin && { label: "Pay Chanda", to: "/pay", icon: <FiDollarSign /> },
    !isAdmin && {
      label: "Transactions",
      to: "/transactions",
      icon: <FiList />,
    },
    !isAdmin &&
      isMaal && {
        label: "Muqami Dashboard",
        to: "/muqami-dashboard",
        icon: <FiBarChart2 />,
      },
    !isAdmin && { label: "Profile", to: "/profile", icon: <FiUser /> },
  ].filter(Boolean);

  const navItemsMobile = [
    { label: "Dashboard", to: "/", icon: <FiHome /> },
    isAdmin && {
      label: "Transactions",
      to: "/admin/transactions",
      icon: <FiList />,
    },
    isAdmin && {
      label: "Access",
      to: "/admin/user-access",
      icon: <FiUsers />,
    },
    !isAdmin && { label: "Pay", to: "/pay", icon: <FiDollarSign /> },
    !isAdmin && {
      label: "Transactions",
      to: "/transactions",
      icon: <FiList />,
    },
    !isAdmin &&
      isMaal && {
        label: "Muqami",
        to: "/muqami-dashboard",
        icon: <FiBarChart2 />,
      },
    { label: "User", to: "/profile", icon: <FiUser /> },
  ].filter(Boolean);

  const isActiveRoute = (to) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/profile") {
      return location.pathname === "/profile" || location.pathname === "/edit-profile";
    }
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const accountType = isAdmin
    ? "Admin Account"
    : isMaal
    ? "Muqami Maal Account"
    : "Personal Account";

  return (
    <>
      {/* Sidebar */}
      <aside className="z-50 hidden h-full w-72 rounded-3xl bg-emerald-950 p-3 text-white shadow-xl md:block">
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="rounded-3xl bg-white p-4 text-emerald-950 shadow-sm">
            <div className="flex items-center gap-3">
              <img
                src="/Khuddam-Logo-Word.svg"
                alt="Khuddam Logo"
                className="h-11"
              />
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-700">
                  Khuddam
                </p>
                <h2 className="text-lg font-black leading-tight">
                  Chanda
                </h2>
              </div>
            </div>
          </div>

          {/* User Section */}
          <Link
            to={"/edit-profile"}
            className="mt-4 flex items-center gap-3 rounded-3xl border border-emerald-800 bg-emerald-900/80 p-4 transition hover:border-lime-300 hover:bg-emerald-900"
          >
            <div className="rounded-2xl bg-lime-300 p-3 text-emerald-950">
              <FiUser className="text-xl" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-black">
                {user?.firstName || "Welcome"}
              </p>
              <p className="text-xs font-medium text-emerald-100">
                {accountType}
              </p>
            </div>
          </Link>

          {/* Menu Section */}
          <div className="px-3 pt-7 text-xs font-black uppercase tracking-[0.2em] text-lime-200">
            Menu
          </div>
          <ul className="mt-3 space-y-2">
            {navItems.map((item) => {
              const active = isActiveRoute(item.to);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold transition ${
                      active
                        ? "bg-lime-300 text-emerald-950 shadow"
                        : "text-emerald-50 hover:bg-emerald-900 hover:text-white"
                    }`}
                  >
                    <span
                      className={`text-lg ${
                        active ? "text-emerald-950" : "text-lime-200"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Others Section at bottom */}
          <div className="mt-auto">
            <div className="px-3 pb-3 text-xs font-black uppercase tracking-[0.2em] text-lime-200">
              Others
            </div>
            <button
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-2xl border border-red-200/20 px-4 py-3 text-left text-sm font-bold text-red-100 transition hover:bg-red-500/10 hover:text-red-50"
            >
              <FiLogOut className="text-lg" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Bottom Nav (mobile) */}
      <div className="sticky bottom-0 z-50 bg-[#f6f3ea]/95 px-3 pb-3 pt-2 shadow-[0_-12px_30px_rgba(15,23,42,0.08)] backdrop-blur md:hidden">
        <div className="rounded-3xl border border-emerald-100 bg-white p-2 shadow-lg">
          <ul className="flex items-stretch justify-between gap-1">
            {navItemsMobile.map((item) => {
              const active = isActiveRoute(item.to);
              return (
                <li key={item.to} className="min-w-0 flex-1">
                  <button
                    type="button"
                    className={`flex w-full flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-xs font-bold transition ${
                      active
                        ? "bg-emerald-950 text-lime-200 shadow"
                        : "text-zinc-500 hover:bg-emerald-50 hover:text-emerald-800"
                    }`}
                    onClick={() => navigate(item.to)}
                    aria-current={active ? "page" : undefined}
                  >
                    <span
                      className={`text-xl ${
                        active ? "text-lime-200" : "text-emerald-700"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span className="truncate">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
